import { useMutation, useQuery, keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { extractErrorMessage } from '@/lib/api';

import {
  createAdminUser,
  getAdminUsers,
  getAdminUserById,
  getUserStats,
  updateUser,
  deleteUser,
  forceUserPassword,
  grantGiftCredit,
  getUserWalletTransactions,
  type CreateAdminUserInput,
  type UpdateAdminUserInput,
  type AdminUserQueryParams,
  type GrantGiftCreditPayload,
} from '../services/user-admin.service';

export function useAdminUsers(params?: AdminUserQueryParams) {
  return useQuery({
    queryKey: ['users', 'admin', params],
    queryFn: () => getAdminUsers(params),
    placeholderData: keepPreviousData,
  });
}

/** Real platform user counts for the admin home dashboard. */
export function useUserStats() {
  return useQuery({
    queryKey: ['users', 'stats'],
    queryFn: getUserStats,
  });
}

export function useAdminUserById(id: string) {
  return useQuery({
    queryKey: ['users', 'admin', id],
    queryFn: () => getAdminUserById(id),
    enabled: !!id,
  });
}

export function useCreateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAdminUserInput) => createAdminUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'admin'] });
    },
  });
}

/** Update a user (status, role, profile). Invalidates the users list + detail on success. */
export function useUpdateAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAdminUserInput }) =>
      updateUser(id, payload),
    onSuccess: (_data, { id }) => {
      toast.success('Utente aggiornato');
      queryClient.invalidateQueries({ queryKey: ['users', 'admin'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'admin', id] });
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error));
    },
  });
}

/** Permanently delete a user. Invalidates the users list on success. */
export function useDeleteAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      toast.success('Utente eliminato');
      queryClient.invalidateQueries({ queryKey: ['users', 'admin'] });
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error));
    },
  });
}

/** Super-admin only: force a user's password. */
export function useForceUserPassword() {
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      forceUserPassword(id, password),
  });
}

/** Super-admin only: grant a no-expiry gift credit to a user's bonus wallet. */
export function useGrantGiftCredit(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GrantGiftCreditPayload) => grantGiftCredit(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'admin', userId] });
      toast.success('Credito omaggio accreditato');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error));
    },
  });
}

export function useUserWalletTransactions(userId: string, page: number) {
  return useQuery({
    queryKey: ['admin-user-wallet-tx', userId, page],
    queryFn: () => getUserWalletTransactions(userId, page),
    placeholderData: keepPreviousData,
  });
}
